import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { ChartRange, CHART_RANGES } from './chart.utils';

/**
 * Deep links: the URL fragment mirrors what is on screen, so a link (or a reload, or back / forward)
 * shows the same tab, the same opened popup and the same chart selection.
 *
 *   #<tab>[/<view>…][?<key>=<value>&…]
 *
 *   #heizung                                      the tab (old links "#heizung" keep working)
 *   #heizung/hc/Heizkreis%201?range=week          a view (popup / sub-tab) inside it, path-like
 *   #klima/room/knx-f470?from=1792814400&to=1792837800&hide=valve
 *   #knx?knx.overview.hide=valve                  a chart that is not the open view: its keys prefixed
 *
 * - The path is hierarchical: tab / sub-tab / popup … — a component reads the segment(s) below its own
 *   prefix ({@link childOf}) and closing a popup goes back to its parent path ({@link closeView}).
 * - Chart state keys (range, from, to, hide, show) belong to the chart whose id is the open view's
 *   path (unprefixed); any other chart writes them as "<chartId>.<key>" ({@link chartKey}).
 *   Unprefixed keys are dropped when the view changes; prefixed ones of other tabs when the tab changes.
 * - Times (from / to) are Unix seconds (minute precision); epoch ms and the older local
 *   "YYYY-MM-DDTHH:mm" are still read.
 * - Values are written so that Angular's router (which re-serialises the fragment with
 *   encodeURI(decodeURIComponent(…)) on load and on back / forward) leaves them unchanged:
 *   characters that would break the syntax are escaped with "~" ({@link escapePart}).
 *
 * Changes made by the app replace the current history entry (a tab switch pushes one, as before; a
 * popup opened by a tap pushes one too, so Back closes it — popup-card/sheet-history.ts);
 * a fragment the browser navigates to (back / forward, an edited or clicked link) is parsed and
 * announced on {@link external$}, and components re-apply it.
 *
 * The links in use (views = path, keys = query):
 *   energie   energie/<pvYield|battery|grid|consumption> card popups, energie/chart (📈); energie.range
 *   heizung   heizung/<boiler|buffer|water|solarThermal|outdoor|pump|pellet|hydraulics>, heizung/hc/<circuit>,
 *             heizung/settings (the Fröling's settings + change log, no chart), heizung/chart (📈); heizung.range
 *   klima     KNX room temperatures: klima/room/<deviceId> room popup; klima.range, klima.humidity
 *   knx       knx/<lights|sockets|blinds|weather> sub-tabs (a bare "knx" = the stored one),
 *             knx/weather/<series>; knx.lights.onlyOn, knx.sockets.onlyOn
 *             (old links knx/room/<id>, knx/temperatures, knx.range, knx.humidity → Klima, and the
 *             Govee popups klima/<sensor> → klima: {@link migrate}, the URL is rewritten on arrival)
 *   analyse   the Analyse board (BoardService): analyse/<slot id> = an admin's saved analysis (a view path
 *             only: its charts, window, legend and text are on the server; without a session a note);
 *             a bare "analyse" = the admin's active slot (rewritten to analyse/<id>) or a note.
 *             Read-only snapshot links (🔗 / PDF of a board; links shared before the slots too):
 *             c=<chart ids>, from, to (older links: r=<preset>), title, note — tab-root keys (dropped when
 *             another tab is chosen); per chart analyse.<chart id>.hide/show (chart ids: board-catalog.ts;
 *             valves.all = all valves' heatmap + evaluation, its order analyse.valves.all.sort;
 *             valves.<floor> = one line per room, e.g. valves.E1). Nothing writes these keys any more
 *             but BoardService.snapshotLink(): the board's legend and window are not mirrored in the URL.
 *   zisterne  zisterne/level (own range, default week)
 *   statistik statistik/<YYYY-MM-DD> hourly overlay (field=…), statistik/faults; statistik.year, .month,
 *             .view, .broken, .sort, .pelletView, .pelletSort
 *   muell     muell/<type>
 *   global    admin=<logs|clients|knx|cams>, admin.level, admin.knx(.…), admin.cams.cam;
 *             overlay=changelog|api|boardPicker
 *   charts    range, from, to, hide, show — unprefixed for the open view's chart, else "<chartId>.<key>"
 */

/** The dashboard's tabs: the first path segment of a deep link. */
export const LINK_TABS = ['energie', 'klima', 'heizung', 'knx', 'zisterne', 'muell', 'netzwerk', 'statistik', 'analyse'] as const;
export type LinkTab = typeof LINK_TABS[number];

/** Keys that belong to no view and no chart (they survive view and tab changes). Prefixed keys whose
 *  first part is not a tab (e.g. "admin.knx") are kept on tab changes too. */
const GLOBAL_KEYS = new Set<string>(['admin', 'overlay']);
/** Path segments below a tab that are views of their own (sub-tabs): closing a popup inside one
 *  goes back to it; any other intermediate segment ("klima/room", "heizung/hc") is skipped. */
const SUB_VIEWS: Record<string, readonly string[]> = { knx: ['lights', 'sockets', 'blinds', 'weather'] };

/**
 * Links of views that moved (old links keep working): the KNX room temperatures moved from the KNX tab
 * ("knx/temperatures", "knx/room/<id>", a bare "knx" with knx.range / knx.humidity, which opened the
 * Temperaturen sub-tab) to Klima, and the Govee sensor popups ("klima/<sensor id>") are gone (2026-10-21).
 * Returns true if it changed `st` (the URL is then rewritten in the new form).
 */
function migrate(st: LinkState): boolean {
  let changed = false;
  const oldKeys = st.params.has('knx.range') || st.params.has('knx.humidity');
  if (st.tab === 'knx' && (st.path[0] === 'room' || st.path[0] === 'temperatures' || (!st.path.length && oldKeys))) {
    st.tab = 'klima';
    st.path = st.path[0] === 'room' ? st.path : [];
    changed = true;
  }
  if (st.tab === 'klima' && st.path.length && st.path[0] !== 'room') {
    st.path = [];   // with the popup's own keys (unprefixed: its chart's range, zoom, mode …)
    for (const k of [...st.params.keys()]) if (!k.includes('.') && !GLOBAL_KEYS.has(k)) st.params.delete(k);
    changed = true;
  }
  for (const [from, to] of [['knx.range', 'klima.range'], ['knx.humidity', 'klima.humidity']]) {
    const v = st.params.get(from);
    if (v != null) { st.params.delete(from); if (!st.params.has(to)) st.params.set(to, v); changed = true; }
  }
  return changed;
}
/** Order of the chart keys in a written URL (all others follow alphabetically). */
const KEY_ORDER = ['range', 'from', 'to', 'hide', 'show'];

/** Visible time window of a chart, epoch ms. */
export interface LinkWindow { from: number; to: number; }

interface LinkState { tab: string | null; path: string[]; params: Map<string, string>; }

// ── codec ────────────────────────────────────────────────────────────────────

const ESC: Record<string, string> = { '~': '~~', '/': '~s', '&': '~a', '=': '~e', '#': '~h', '?': '~q', ',': '~c', '%': '~p' };
const UNESC: Record<string, string> = Object.fromEntries(Object.entries(ESC).map(([k, v]) => [v[1], k]));

/** One path segment / key / value / list item → URL text that survives Angular's fragment round trip. */
export function escapePart(s: string): string {
  return encodeURI(s.replace(/[~/&=#?,%]/g, c => ESC[c]));
}

/** Inverse of {@link escapePart}; tolerant of foreign percent-escapes. */
export function unescapePart(s: string): string {
  let d = s;
  try { d = decodeURIComponent(s); } catch { /* keep raw */ }
  return d.replace(/~(.)/g, (m, c) => UNESC[c] ?? m);
}

/** A fragment → its link state, old links migrated (`migrated`: the fragment was in an old form). */
function parseFragment(hash: string): LinkState & { migrated: boolean } {
  const raw = hash.replace(/^#/, '');
  const q = raw.indexOf('?');
  const pathPart = q >= 0 ? raw.slice(0, q) : raw;
  const queryPart = q >= 0 ? raw.slice(q + 1) : '';
  const segs = pathPart.split('/').filter(s => s !== '').map(unescapePart);
  const params = new Map<string, string>();
  for (const pair of queryPart.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const k = unescapePart(eq >= 0 ? pair.slice(0, eq) : pair);
    const v = eq >= 0 ? pair.slice(eq + 1) : '';
    // list values keep their raw "," separators: split before unescaping each item
    if (k) params.set(k, v.split(',').map(unescapePart).join('\u0000'));
  }
  const tab = segs.length && (LINK_TABS as readonly string[]).includes(segs[0]) ? segs[0] : null;
  const st: LinkState = { tab, path: tab ? segs.slice(1) : [], params };
  const migrated = migrate(st);
  return { ...st, migrated };
}

/** The view path (tab included, e.g. "klima/room/knx-f470"; "" for none) of a fragment, old links
 *  migrated — for code that reads another history entry's fragment (popup-card/sheet-history.ts). */
export function linkPathOf(hash: string): string {
  const st = parseFragment(hash);
  return st.tab ? [st.tab, ...st.path].join('/') : '';
}

function sortKeys(keys: string[]): string[] {
  const rank = (k: string): number => {
    const i = KEY_ORDER.indexOf(k);
    return i >= 0 ? i : (k.includes('.') ? 200 : 100);
  };
  return keys.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

function buildFragment(st: LinkState): string {
  const segs = st.tab ? [st.tab, ...st.path] : [];
  let out = segs.map(escapePart).join('/');
  const keys = sortKeys([...st.params.keys()]);
  if (keys.length) {
    out += '?' + keys.map(k => {
      const v = st.params.get(k)!;
      return escapePart(k) + (v === '' ? '' : '=' + v.split('\u0000').map(escapePart).join(','));
    }).join('&');
  }
  return out ? '#' + out : '';
}

/** A time in a link (from / to): Unix seconds. */
export function linkStamp(ms: number): string {
  return String(Math.floor(ms / 1000));
}

/** Local "YYYY-MM-DDTHH:mm" of an epoch-ms time (the value of a datetime-local input). */
export function formatLinkTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** A link time → epoch ms, else null: Unix seconds (what links carry), epoch ms (13+ digits), or the
 *  older local "YYYY-MM-DD[THH:mm[:ss]]" of links shared before 2026-10-21. */
export function parseLinkTime(s: string | null | undefined): number | null {
  if (!s) return null;
  if (/^\d{12,}$/.test(s)) return Number(s);
  if (/^\d{1,11}$/.test(s)) return Number(s) * 1000;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(s);
  if (!m) return null;
  const d = new Date(2000, 0, 1, +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  d.setFullYear(+m[1], +m[2] - 1, +m[3]);   // the Date constructor would read years 0–99 as 19xx
  const t = d.getTime();
  return isNaN(t) ? null : t;
}

// ── service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class DeepLinkService implements OnDestroy {
  /** The one instance, for the Chart.js plugin (which lives outside Angular's injector). */
  static instance: DeepLinkService | null = null;

  /** The browser navigated to another fragment (back / forward, an edited or clicked link): re-apply. */
  readonly external$ = new Subject<void>();
  /** Any change (the app's own and external ones). */
  readonly changed$ = new Subject<void>();

  private st: LinkState;
  private writeTimer: any = null;
  private lastWritten: string;
  private readonly onNav = () => this.zone.run(() => this.readUrl());

  constructor(private readonly zone: NgZone) {
    DeepLinkService.instance = this;
    this.st = this.adopt(location.hash);
    this.lastWritten = location.hash;
    // A bfcache reload (AppComponent.onPageShow) adds "?_t=…" to the real query: not part of a link
    if (/[?&]_t=/.test(location.search)) {
      const search = location.search.replace(/([?&])_t=[^&]*&?/, '$1').replace(/[?&]$/, '');
      history.replaceState(history.state, '', location.pathname + search + location.hash);
    }
    window.addEventListener('popstate', this.onNav);
    window.addEventListener('hashchange', this.onNav);
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.onNav);
    window.removeEventListener('hashchange', this.onNav);
    if (DeepLinkService.instance === this) DeepLinkService.instance = null;
  }

  // ── reading ────────────────────────────────────────────────────────────────

  /** The tab in the link (null = none / unknown). */
  get tab(): LinkTab | null { return this.st.tab as LinkTab | null; }

  /** The open view's full path including the tab ("heizung/hc/Heizkreis 1"), or null for none. */
  get view(): string | null { return this.st.tab && this.st.path.length ? [this.st.tab, ...this.st.path].join('/') : null; }

  /** True if `path` (a view path including its tab) is open itself or as a parent of the open view. */
  isOpen(path: string): boolean {
    const v = this.view;
    return !!v && (v === path || v.startsWith(path + '/'));
  }

  /** The segment right below `prefix` in the open view ("klima/room" → "knx-f470"), else null.
   *  `prefix` may be just a tab ("knx" → the KNX sub-view). */
  childOf(prefix: string): string | null {
    const full = this.st.tab ? [this.st.tab, ...this.st.path] : [];
    const pre = prefix.split('/');
    if (full.length <= pre.length) return null;
    for (let i = 0; i < pre.length; i++) if (full[i] !== pre[i]) return null;
    return full[pre.length];
  }

  /** A plain key (e.g. "admin" or "knx.filter"). */
  get(key: string): string | null {
    const v = this.st.params.get(key);
    return v == null ? null : v.replace(/\u0000/g, ',');
  }

  /** A list key ("a,b,c" → ["a","b","c"]); items may contain commas (escaped in the URL). */
  getList(key: string): string[] {
    const v = this.st.params.get(key);
    return v == null || v === '' ? [] : v.split('\u0000');
  }

  /** The current link (absolute URL) — for the copy-link buttons. */
  href(): string {
    this.flush();
    return location.href;
  }

  // ── writing ────────────────────────────────────────────────────────────────

  /** Set (or with null / '' remove) a plain key. */
  set(key: string, value: string | number | null | undefined): void {
    if (value == null || value === '') this.st.params.delete(key);
    else this.st.params.set(key, String(value));
    this.scheduleWrite();
  }

  /** Set (or with an empty list remove) a list key. */
  setList(key: string, items: string[]): void {
    if (!items.length) this.st.params.delete(key);
    else this.st.params.set(key, items.join('\u0000'));
    this.scheduleWrite();
  }

  /** Switch the tab (pushes a history entry, like the old `location.hash = tab`): closes any view and
   *  drops the chart keys of the other tabs. */
  setTab(tab: LinkTab): void {
    if (this.st.tab === tab && !this.st.path.length) return;
    this.flush();   // a pending change belongs to the entry being left
    const leftRoot = this.st.tab !== tab && !this.st.path.length;
    this.st.tab = tab;
    this.setPath([]);
    for (const k of [...this.st.params.keys()]) {
      const owner = k.split('.')[0];
      if (k.includes('.') && owner !== tab && (LINK_TABS as readonly string[]).includes(owner)) this.st.params.delete(k);
      // a tab's own root keys (e.g. the Analyse board's c / from / to) stay with that tab
      else if (leftRoot && !k.includes('.') && !GLOBAL_KEYS.has(k)) this.st.params.delete(k);
    }
    this.write(true);
  }

  /** Open a view: `path` includes its tab ("heizung/boiler", "klima/room/knx-f470"); the tab follows it. */
  openView(path: string): void {
    const segs = path.split('/').filter(Boolean);
    if (!segs.length || !(LINK_TABS as readonly string[]).includes(segs[0])) return;
    if (this.view === segs.join('/')) return;
    this.st.tab = segs[0];
    this.setPath(segs.slice(1));
    this.scheduleWrite();
  }

  /** Close a view (and anything opened inside it): the path goes back to the view's parent.
   *  No-op if `path` is not open (e.g. another popup has replaced it meanwhile). */
  closeView(path: string): void {
    if (!this.isOpen(path)) return;
    const segs = path.split('/').filter(Boolean);
    const parent = segs.slice(1, -1);
    // "klima/room/<id>" → "klima", but "knx/weather/<series>" → "knx/weather"
    while (parent.length && !(parent.length === 1 && (SUB_VIEWS[segs[0]] ?? []).includes(parent[0]))) parent.pop();
    this.setPath(parent);
    this.scheduleWrite();
  }

  private setPath(path: string[]): void {
    const changed = path.join('/') !== this.st.path.join('/');
    this.st.path = path;
    if (!changed) return;
    // the old view's own keys (unprefixed) go with it
    for (const k of [...this.st.params.keys()]) if (!k.includes('.') && !GLOBAL_KEYS.has(k)) this.st.params.delete(k);
  }

  // ── chart state ────────────────────────────────────────────────────────────

  /** The key a chart's `name` (range/from/to/hide/show) is stored under: plain for the open view's
   *  own chart (chart id = the view path), "<chartId>.<name>" for any other chart. */
  chartKey(chartId: string, name: string): string {
    return chartId === this.view ? name : `${chartId}.${name}`;
  }

  /** The chart's linked range, or null (= its default). */
  range(chartId: string): ChartRange | null {
    const v = this.get(this.chartKey(chartId, 'range'));
    return CHART_RANGES.some(r => r.key === v) ? v as ChartRange : null;
  }

  /** Store the chart's range (the default is not written). A new range drops a zoom window. */
  setRange(chartId: string, range: ChartRange, defaultRange: ChartRange = 'day'): void {
    const changed = (this.range(chartId) ?? defaultRange) !== range;
    this.set(this.chartKey(chartId, 'range'), range === defaultRange ? null : range);
    if (changed) this.setZoom(chartId, null);
  }

  /** The chart's linked zoom window (x axis, epoch ms), or null. */
  zoom(chartId: string): LinkWindow | null {
    const from = parseLinkTime(this.get(this.chartKey(chartId, 'from')));
    const to = parseLinkTime(this.get(this.chartKey(chartId, 'to')));
    return from != null && to != null && to > from ? { from, to } : null;
  }

  /** Store (null: drop) the chart's zoom window, rounded outward to whole minutes. */
  setZoom(chartId: string, win: LinkWindow | null): void {
    const minute = 60_000;
    const from = win ? Math.floor(win.from / minute) * minute : null;
    let to = win ? Math.ceil(win.to / minute) * minute : null;
    if (from != null && to != null && to <= from) to = from + minute;
    this.set(this.chartKey(chartId, 'from'), from == null ? null : linkStamp(from));
    this.set(this.chartKey(chartId, 'to'), to == null ? null : linkStamp(to));
  }

  /** Datasets toggled away from their default visibility: key → hidden. */
  visibility(chartId: string): Map<string, boolean> {
    const m = new Map<string, boolean>();
    for (const k of this.getList(this.chartKey(chartId, 'show'))) m.set(k, false);
    for (const k of this.getList(this.chartKey(chartId, 'hide'))) m.set(k, true);
    return m;
  }

  /** Store the datasets toggled away from their default (hidden=true in "hide", false in "show"). */
  setVisibility(chartId: string, toggled: Map<string, boolean>): void {
    const hide: string[] = [], show: string[] = [];
    toggled.forEach((hidden, key) => (hidden ? hide : show).push(key));
    this.setList(this.chartKey(chartId, 'hide'), hide);
    this.setList(this.chartKey(chartId, 'show'), show);
  }

  /** Drop every key that starts with `prefix` (e.g. "admin." when the admin overlay closes). */
  clearPrefix(prefix: string): void {
    let changed = false;
    for (const k of [...this.st.params.keys()]) if (k.startsWith(prefix)) { this.st.params.delete(k); changed = true; }
    if (changed) this.scheduleWrite();
  }

  /** Drop every key of a chart (e.g. when its data set changes completely). */
  clearChart(chartId: string): void {
    for (const n of KEY_ORDER) this.set(this.chartKey(chartId, n), null);
  }

  /**
   * Write the link into the current history entry right away (even when the URL already matches) and
   * let the components re-apply it, as after a browser navigation. For a popstate handler that adjusts
   * the state of the entry the browser just went to (popup-card/sheet-history.ts).
   */
  commitAndAnnounce(): void {
    if (this.writeTimer) { clearTimeout(this.writeTimer); this.writeTimer = null; }
    const frag = buildFragment(this.st);
    try { history.replaceState(history.state, '', location.pathname + location.search + frag); } catch { /* rate limit */ }
    this.lastWritten = location.hash;
    this.external$.next();
    this.changed$.next();
  }

  // ── URL sync ───────────────────────────────────────────────────────────────

  /** Coalesce the app's changes (a legend click, a zoom …) into one history.replaceState. */
  private scheduleWrite(): void {
    this.changed$.next();
    if (this.writeTimer) return;
    this.writeTimer = setTimeout(() => { this.writeTimer = null; this.write(false); }, 120);
  }

  private flush(): void {
    if (this.writeTimer) { clearTimeout(this.writeTimer); this.writeTimer = null; this.write(false); }
  }

  private write(push: boolean): void {
    if (this.writeTimer) { clearTimeout(this.writeTimer); this.writeTimer = null; }
    const frag = buildFragment(this.st);
    if (!push && frag === location.hash) return;
    const url = location.pathname + location.search + (frag || '');
    try {
      if (push) history.pushState(history.state, '', url);
      else history.replaceState(history.state, '', url);
    } catch { /* Safari's history rate limit: the next change writes again */ }
    this.lastWritten = location.hash;
    if (push) this.changed$.next();
  }

  /** The browser moved to another fragment: adopt it (unless it is the one just written). */
  private readUrl(): void {
    if (location.hash === this.lastWritten && !this.writeTimer) return;
    if (this.writeTimer) { clearTimeout(this.writeTimer); this.writeTimer = null; }
    this.st = this.adopt(location.hash);
    this.lastWritten = location.hash;
    this.external$.next();
    this.changed$.next();
  }

  /** Parse the URL's fragment; an old link is rewritten in its new form right away (in the current
   *  history entry), so Back, the copy-link buttons and later reads all see the new one. */
  private adopt(hash: string): LinkState {
    const { migrated, ...st } = parseFragment(hash);
    if (migrated) {
      try { history.replaceState(history.state, '', location.pathname + location.search + buildFragment(st)); } catch { /* rate limit */ }
    }
    return st;
  }
}
